import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { BusinessEntityService } from '../../../core/services/business.service';
import BusinessEntity from '../../../core/models/business.model';
import { FarmSiteService } from '../../../core/services/farm-site.service';
import { PondCultureService } from '../../../core/services/pond-culture.service';
import CultureSummaryReportModel from '../../../core/models/culture-summary.model';
import { CultureSummaryReportService } from '../../../core/services/culture-summary.service';
import FarmSiteModel from 'src/app/core/models/farm-site.model';
import PondCultureModel from 'src/app/core/models/pond-culture.model';
import * as Chart from 'chart.js';
import * as _ from 'lodash';
import { ActivatedRoute } from '@angular/router';
import { ExcelService } from 'src/app/core/services/excel.service';
import { PersonsService } from '../../../core/services/persons.service';
import PersonsModel from '../../../core/models/persons.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from 'src/app/core/utilities/batch-loader.util';
import { USER_SESSION_SERVICE } from 'src/app/core/rbacl/user-session.service';
import { BUSINESS_CATEGORY_KEYS, PERSON_ROLE_KEYS } from 'src/app/core/constants/app-constants';

declare var $: any;

/**
 * CultureSummaryReportComponent
 * 
 * A comprehensive reporting component for displaying aquaculture culture summary data
 * with advanced filtering, searching, pagination, sorting, and data visualization capabilities.
 * Provides detailed culture analytics with feed log vs DOC and ABW vs DOC charts.
 * 
 * Key responsibilities:
 * - Display culture summary reports with dynamic data loading
 * - Provide hierarchical selection (business → farm site)
 * - Filter and search culture records by multiple criteria
 * - Sort records by pond name, culture identifier, start/end dates, stocking size
 * - Generate Feedlog vs DOC and ABW vs DOC charts using Chart.js
 * - Export report data to Excel
 * - Support infinite scroll pagination with batch loading
 * - Display data with fullscreen view capability
 * 
 * User roles supported:
 * - Business Admin (Farm): Full access to their farm business
 * - Farmer: Access to own farm business
 * - Farm Manager: Access assigned farm with manager-specific views
 * - Business Admin (Provider): Access to farmer businesses
 * - Provider Manager: Access to technician-assigned farms
 * - Employee: Access to all businesses with business selection required
 * 
 * Features:
 * - Dynamic business dropdown filtering
 * - Auto-selection of single options (farm site)
 * - Infinite scroll pagination with batch loading
 * - Real-time search with multi-field matching
 * - Filter by specific criteria with combined search-filter
 * - Multiple column sorting (ascending/descending)
 * - Chart visualization for culture analytics
 * - Excel export with selected criteria
 * - Responsive layout with fullscreen support
 * - History tracking for navigation
 * 
 * Data visualization:
 * - Feedlog vs DOC: Line chart showing feed data per day of culture
 * - ABW vs DOC: Line chart showing average body weight per day of culture
 * 
 * Search fields:
 * - Pond/section name
 * - Culture name
 * - Hatchery name
 * - Breeder name
 * - Breed line name
 * 
 * Sorting capabilities:
 * - Pond name (ascending/descending)
 * - Culture identifier (ascending/descending)
 * - Start date (ascending/descending)
 * - End date (ascending/descending)
 * - Stocking size (ascending/descending)
 * 
 * @component
 * @selector app-culture-summary-report
 * @example
 * <app-culture-summary-report></app-culture-summary-report>
 * 
 * @implements {OnInit, OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-culture-summary-report',
  templateUrl: './culture-summary-report.component.html',
  styleUrls: ['./culture-summary-report.component.scss']
})
export class CultureSummaryReportComponent implements OnInit, OnDestroy {
  // ============ Report Data Models ============
  /** 
   * Array of culture summary reports
   * Paginated data displayed in the report table
   * @type {Array<CultureSummaryReportModel>}
   */
  public cultureSummaryReports: Array<CultureSummaryReportModel>;

  /** 
   * Current culture summary report model
   * Used for form binding and report requests
   * @type {CultureSummaryReportModel}
   */
  public cultureSummaryReport: CultureSummaryReportModel;

  /** 
   * Original/full list of culture summary reports before filtering
   * Used for search and filter operations
   * @type {Array<CultureSummaryReportModel>}
   */
  public originalcultureSummaryReports: {} & Array<CultureSummaryReportModel>;

  // ============ Business and Farm Data ============
  /** 
   * Array of business entities
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;

  /** 
   * Currently selected business entity
   * @type {BusinessEntity}
   */
  public businessEntity: BusinessEntity;

  /** 
   * Array of farm sites for selected business
   * @type {Array<FarmSiteModel>}
   */
  public farmSites: Array<FarmSiteModel>;

  /** 
   * Pond culture data including feed logs and sampling details
   * @type {PondCultureModel}
   */
  public pondCulture: PondCultureModel;

  /** 
   * Array of persons/technicians for role-based access
   * @type {Array<PersonsModel>}
   */
  public persons: Array<PersonsModel>;

  // ============ UI State and Control ============
  /** 
   * Flag indicating if search has been clicked/executed
   * @type {boolean}
   */
  public isSearchClicked: boolean;

  /** 
   * Loading indicator for data retrieval
   * @type {boolean}
   */
  public responseLoader: boolean;

  /** 
   * Name of exported Excel file
   * @type {string}
   */
  public file_name: string;

  /** 
   * Key for sorting operations
   * @type {string}
   * @default 'name'
   */
  public key: string = 'name';

  /** 
   * Reverse order flag for sorting
   * @type {boolean}
   * @default false
   */
  public reverse: boolean = false;

  // ============ Chart-related Properties ============
  /** 
   * Canvas element reference for charts
   * @type {any}
   */
  public canvas: any;

  /** 
   * Canvas 2D context for chart drawing
   * @type {any}
   */
  public ctx: any;

  /** 
   * Document object reference
   * @type {any}
   */
  public doc: any;

  /** 
   * Flag indicating if feedlog vs DOC chart has no data
   * @type {boolean}
   */
  public myChartNodata: boolean;

  /** 
   * Flag indicating if sampling vs DOC chart has no data
   * @type {boolean}
   */
  public mySamplingvsDocData: boolean;

  // ============ Sorting State Flags ============
  /** 
   * Flag for pond name ascending sort
   * @type {boolean}
   */
  public nameAsc: boolean;

  /** 
   * Flag for culture identifier ascending sort
   * @type {boolean}
   */
  public cultureIdentifierAsc: boolean;

  /** 
   * Flag for start date ascending sort
   * @type {boolean}
   */
  public startDateAsc: boolean;

  /** 
   * Flag for end date ascending sort
   * @type {boolean}
   */
  public endDateAsc: boolean;

  /** 
   * Flag for stocking size ascending sort
   * @type {boolean}
   */
  public stockingSizeAsc: boolean;

  // ============ Search and Filter ============
  /** 
   * Application permissions object
   * @type {any}
   */
  public appPermission: any;

  /** 
   * Search keyword/text
   * @type {string}
   */
  public keyword: string;

  /** 
   * Current item being viewed/selected
   * @type {any}
   */
  public item: any;

  /** 
   * Flag indicating no data available for display
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /** 
   * Current active page number for pagination
   * @type {number}
   */
  public activePageNo: number;

  /** 
   * Flag to clear search input field
   * @type {boolean}
   */
  public clearSearchInput: boolean;

  /** 
   * Reactive form group for business/farm site selection
   * Controls: businessfield, locationField
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /** 
   * Flag for business dropdown open/close state
   * @type {boolean}
   * @default false
   */
  public isDropdownOpen = false;

  /** 
   * Filtered list of business options for dropdown
   * @type {Array<any>}
   */
  public filteredOptions: any[] = [];

  /** 
   * Placeholder text for business dropdown search
   * @type {string}
   * @default 'Enter the business name'
   */
  public businessSearch: string = 'Enter the business name';

  /** 
   * Flag indicating if form has been submitted
   * @type {boolean}
   */
  public submitted: boolean;

  /** 
   * Flag to disable submit button during submission
   * @type {boolean}
   * @default false
   */
  public submitButtonDisabled: boolean = false;

  /** 
   * Search text input
   * @type {string}
   */
  public search: string;

  /** 
   * Filter type for specialized filtering
   * @type {string}
   */
  public filterType: string;

  /** 
   * Flag indicating if filters have been applied
   * @type {boolean}
   */
  public filtersApplied: boolean;

  /** 
   * Flag indicating if submit button was clicked
   * Used to show/hide results
   * @type {boolean}
   */
  public isSubmitClicked: boolean;

  // ============ Pagination and Loading ============
  /** 
   * Show/hide list loader spinner
   * @type {boolean}
   * @default false
   */
  public listLoader = false;

  /** 
   * Tracks batch loading progress
   * @type {number}
   * @default 0
   */
  public startIndex = 0;

  /** 
   * Flag indicating filtering operation in progress
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /** 
   * Flag indicating no data/empty list
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /** 
   * Batch loader utility for infinite scroll pagination
   * Manages loading data in chunks as user scrolls
   * @type {BatchLoader<any>}
   */
  private batchLoader: BatchLoader<any>;

  /** 
   * Full sorted list of culture summary reports
   * Used for sorting operations
   * @type {any}
   */
  sortedFullList: any;

  /**
   * Constructor for CultureSummaryReportComponent
   * 
   * Initializes the component with required services and creates the business/farm site
   * selection form with validation.
   * 
   * Form validation rules:
   * - businessfield: Required (conditionally based on user role)
   * - locationField: Required (farm site selection)
   * 
   * Services injected:
   * - FormBuilder: Creates reactive form groups
   * - ActivatedRoute: Access to route parameters
   * - PersonsService: Retrieve person/technician data
   * - BusinessEntityService: Business entity management
   * - FarmSiteService: Farm site data retrieval
   * - PondCultureService: Pond culture analytics
   * - CultureSummaryReportService: Culture report data
   * - ExcelService: Excel export functionality
   * - CommonMethodsService: Common utility methods
   * - LocationStrategy: Current URL information
   * - PreviousRouteService: Navigation history tracking
   * 
   * @constructor
   * @param {FormBuilder} formBuilder - Angular form builder service
   * @service FormBuilder
   * @param {ActivatedRoute} route - Active route service
   * @service ActivatedRoute
   * @param {PersonsService} personService - Person/technician operations
   * @service PersonsService
   * @param {BusinessEntityService} businessEntityService - Business entity operations
   * @service BusinessEntityService
   * @param {FarmSiteService} farmSitesService - Farm site operations
   * @service FarmSiteService
   * @param {PondCultureService} pondCultureService - Pond culture data operations
   * @service PondCultureService
   * @param {CultureSummaryReportService} cultureSummaryService - Culture summary reports
   * @service CultureSummaryReportService
   * @param {ExcelService} excelService - Excel export service
   * @service ExcelService
   * @param {CommonMethodsService} commonMethodsService - Common utility methods
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Browser location strategy
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Navigation history service
   * @service PreviousRouteService
   */
  constructor(public formBuilder: FormBuilder, public route: ActivatedRoute, public personService: PersonsService, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public pondCultureService: PondCultureService,
    public cultureSummaryService: CultureSummaryReportService, public excelService: ExcelService, public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.cultureSummaryReport = new CultureSummaryReportModel();
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required]
    });
  }

  /**
   * Angular component initialization lifecycle hook
   * 
   * Performs comprehensive initialization:
   * 1. Tracks current route in navigation history
   * 3. Initializes UI state (search, filters, loader flags)
   * 4. Loads business and farm site data based on user role
   * 5. Auto-selects single available options
   * 6. Conditionally disables business field for specific roles
   * 
   * Role-based data loading:
   * - Farmer/Farm Manager/Business Admin (Farm): Load farm sites for their business
   * - Business Admin (Provider): Load all farmer businesses and farm sites
   * - Provider Manager: Load technicians and their assigned farm sites
   * - Employee: Require manual business selection
   * 
   * Auto-selection logic:
   * - If only one farm site exists, auto-select it
   * 
   * Form field behavior:
   * - Farmers/Farm Managers: Business field disabled/readonly
   * - Business Admin (Farm): Business field disabled
   * - Provider/Employees: Business field enabled for selection
   * 
   * 
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook - Called once after component creation
   * @throws {Error} If service calls fail
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.myChartNodata = true;
      this.search = '';
      this.filterType = '';
      this.filtersApplied = false;
      this.isSubmitClicked = false;
      this.isFiltering = false;
      window.scrollTo(0, 0);
      switch (USER_SESSION_SERVICE.currentBusinessCategoryName) {
        case BUSINESS_CATEGORY_KEYS.PROVIDER:
          switch (USER_SESSION_SERVICE.currentUserRole) {
            case PERSON_ROLE_KEYS.BUSINESS_ADMIN:
              this.cultureSummaryReport.business_id = USER_SESSION_SERVICE.currentBusinessId;
              this.farmSites = await this.farmSitesService.getFarmeSitesBusinessDetailsByProviderId(USER_SESSION_SERVICE.currentBusinessId);
              if (this.farmSites.length === 1) {
                this.cultureSummaryReport.farm_site_id = this.farmSites[0].id; // model-binding key
              }
              this.registerForm.get('businessfield').setValidators(null);
              this.registerForm.get('businessfield').updateValueAndValidity();
              break;
            default:
              this.persons = await this.personService.getAllTechniciansByProviderManagerId(USER_SESSION_SERVICE.currentUserId);
              break;
          }
          break;
        default:
          this.businessSearch = undefined;
          this.registerForm.get('businessfield').patchValue('');
          let businesses = await this.businessEntityService.getAllBusiness();
          this.businessEntities = businesses.filter(obj => {
            return ((obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '618a18ac0f339c001e991b08') && (obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '62b3fa8998be51001d17a310'));
          });
          this.filteredOptions = this.businessEntities;
          break;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  isEmployee() {
    return USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.EMPLOYEE;
  }

  isProviderManager() {
    return USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.PROVIDER_MANAGER
  }

  /**
   * Retrieves and loads farm sites for a technician
   * 
   * Used for Provider Manager role to get farm sites assigned to a specific technician.
   * Auto-selects farm site if only one is available.
   * 
   * Process flow:
   * 1. Resets farm site and section selections
   * 2. Calls FarmSiteService to get technician's farm sites
   * 3. Auto-selects farm site if exactly one is available
   * 
   * @async
   * @method getFarmSitesByTechnicianId
   * @returns {Promise<void>}
   * @side-effect Updates farmSites, cultureSummaryReport.farm_site_id
   */
  public async getFarmSitesByTechnicianId() {
    this.cultureSummaryReport.farm_site_id = undefined;
    this.farmSites = await this.farmSitesService.getFarmeSitesByTechnicianId(this.cultureSummaryReport.person_id);
    if (this.farmSites.length === 1) {
      this.cultureSummaryReport.farm_site_id = this.farmSites[0].id; // model-binding key
    }
  }

  /**
   * Getter method to access form controls
   * 
   * Provides convenient access to form controls in templates using the syntax f.controlName.
   * Used for validation error display and field state checking.
   * 
   * Available controls:
   * - f.businessfield: Business selection field
   * - f.locationField: Farm site selection field
   * 
   * @method f
   * @returns {any} - The form controls object with all form field references
   * 
   * @example
   * <div *ngIf="f.businessfield?.invalid && (f.businessfield?.dirty || f.businessfield?.touched)">
   *   Business is required
   * </div>
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * Filters business options based on search text
   * 
   * Implements real-time search/filter for the business dropdown.
   * Compares user input against business names with case-insensitive matching.
   * 
   * Filtering logic:
   * 1. Retrieves current value from businessfield control
   * 2. Converts value to string if it's a BusinessEntity object
   * 3. Compares lowercase business names
   * 4. Updates filteredOptions with matching results
   * 
   * @method filterBusinesses
   * @returns {void}
   * @side-effect Updates filteredOptions array
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * Handles business selection from dropdown
   * 
   * Processes business selection and loads associated farm sites.
   * Auto-selects farm site if only one is available.
   * 
   * @async
   * @method selectOption
   * @param {BusinessEntity} business - The selected business entity
   * @returns {Promise<void>}
   * @side-effect Updates form, loads farm sites, updates UI state
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.cultureSummaryReport.business_id = business.id;
    if (this.cultureSummaryReport.business_id) {
      this.cultureSummaryReport.farm_site_id = undefined;
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmsId(this.cultureSummaryReport.business_id);
      if (this.farmSites.length === 1) {
        this.cultureSummaryReport.farm_site_id = this.farmSites[0].id; // model-binding key
      }
    }
  }

  /**
   * Applies filters and search to culture summary records
   * 
   * Master filter method handling multiple scenarios:
   * 1. Search only: Filters records by search text
   * 2. Filter type only: Filters by selected filter criteria
   * 3. Combined: Applies both search and filter together
   * 4. Neither: Resets to full list with batch loading
   * 
   * Filter operations:
   * - Initiates filtering state
   * - Clears previous results
   * - Applies appropriate filter method
   * - Resets batch loader if needed
   * 
   * Search text normalization:
   * - Removes special characters: [-_ :.]
   * - Converts to lowercase for matching
   * 
   * @method onFiltersApplied
   * @returns {void}
   * @side-effect Updates cultureSummaryReports, batchLoader, filtering state
   */
  public onFiltersApplied() {
    this.isFiltering = true;
    this.cultureSummaryReports = [];
    const value = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
    if (this.search != '') {
      if (this.filterType != '' && this.filtersApplied) {
        this.applySearchWithFilterType();
      } else {
        this.applySearchFilter(value);
      }
    } else {
      if (this.filterType != '' && this.filtersApplied) {
        this.applyFilterWithFilterType();
      } else {
        this.isFiltering = false;
        this.initializeBatchLoader();
        this.batchLoader.reset();
        this.batchLoader.loadNextBatch(true);
      }
    }
  }

  /**
   * Applies search filter to culture summary records
   * 
   * Filters original records based on search text across multiple fields:
   * - section_name: Pond/section name
   * - name: Culture name
   * - Hatcheryname: Hatchery name
   * - breeder_name: Breeder name
   * - breed_line_name: Breed line name
   * 
   * Matching logic:
   * - Case-insensitive comparison
   * - Removes special characters: [-_ :.]
   * - Checks each field for partial matches
   * 
   * @private
   * @method applySearchFilter
   * @param {string} searchValue - Normalized search text to match against
   * @returns {void}
   * @side-effect Updates cultureSummaryReports with filtered results
   */
  private applySearchFilter(searchValue: string) {
    this.cultureSummaryReports = this.originalcultureSummaryReports.filter((cultureDetailsObj: any) =>
      ['section_name', 'name', 'Hatcheryname', 'breeder_name', 'breed_line_name'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], cultureDetailsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })
    );
  }

  /**
   * Applies filter type without search text
   * 
   * Filters records based on selected filter criteria only.
   * Implementation depends on specific filter types defined in filterType property.
   * 
   * @private
   * @method applyFilterWithFilterType
   * @returns {void}
   * @side-effect Updates cultureSummaryReports with filtered results
   */
  private applyFilterWithFilterType() {

  }

  /**
   * Applies both search text and filter type simultaneously
   * 
   * Combines search filtering with specific filter criteria.
   * Implementation depends on specific filter types defined in filterType property.
   * 
   * @private
   * @method applySearchWithFilterType
   * @returns {void}
   * @side-effect Updates cultureSummaryReports with filtered results
   */
  private applySearchWithFilterType() {

  }

  /**
   * Clears applied filters and resets the report view
   * 
   * Removes either search or filter criteria and re-loads data.
   * Allows user to reset individual filters while keeping others.
   * 
   * Clear operations:
   * - 'search': Clears search text and reloads filtered/full list
   * - 'default': Clears filter type and reloads search or full list
   * 
   * @method clearFilters
   * @param {string} filterFrom - Which filter to clear ('search' or other)
   * @returns {void}
   * @side-effect Clears filters, reloads data via onFiltersApplied
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.filterType = '';
        this.filtersApplied = false;
        this.onFiltersApplied();
        break;
    }
  }

  /**
   * Handles culture summary report form submission and data loading
   * 
   * Main report generation flow:
   * 1. Marks all form fields as touched for validation display
   * 2. Validates form structure
   * 3. Returns early if invalid
   * 4. Clears previous data and filters
   * 5. Retrieves business ID for Provider Manager role
   * 6. Loads culture summary data from service
   * 7. Initializes batch loader for pagination
   * 8. Loads first batch of data
   * 9. Marks form as pristine and untouched
   * 10. Shows results and hides submit button
   * 11. Handles errors gracefully
   * 
   * Form validation:
   * - businessfield: Required (conditionally)
   * - locationField: Required (farm site)
   * 
   * UI state during submission:
   * - Loading spinner shown
   * - Submit button disabled
   * - Results initially hidden
   * 
   * Data loading:
   * - Fetches original full list from service
   * - Resets batch loader
   * - Loads first batch (default 20 items)
   * - Available for search/filter operations
   * 
   * @async
   * @method cultureSummary
   * @returns {Promise<void>}
   * @throws {Error} If CultureSummaryReportService call fails
   * @side-effect Loads report data, initializes batch loader, updates UI
   */
  public async cultureSummary() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.cultureSummaryReports = [];
    this.originalcultureSummaryReports = [];
    this.isFiltering = false;
    this.isSubmitClicked = false;
    this.search = '';
    try {
      this.submitButtonDisabled = true;
      this.responseLoader = true;
      if (USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.PROVIDER_MANAGER) {
        let businessId = this.farmSites.find(siteid => siteid.id == this.cultureSummaryReport.farm_site_id);
        this.cultureSummaryReport.business_id = businessId.businessID;
      }
      this.originalcultureSummaryReports = await this.cultureSummaryService.getCultureSummaryReport(this.cultureSummaryReport.business_id, this.cultureSummaryReport.farm_site_id);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.submitted = false;    // Reset validation flag
      this.registerForm.markAsPristine(); // optional - clears dirty flags
      this.registerForm.markAsUntouched(); // optional - clears touched state
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    } finally {
      this.isSubmitClicked = true;
      this.submitButtonDisabled = false;
      this.responseLoader = false;
    }
  }

  /**
   * Window scroll event listener for infinite scroll pagination
   * 
   * Detects when user scrolls near the bottom of the page and
   * automatically loads the next batch of records.
   * 
   * Implementation uses:
   * - HostListener for window:scroll event
   * - Debounce timeout to prevent excessive calls
   * - Bottom threshold of 100px
   * 
   * @method onScroll
   * @returns {void}
   * @side-effect Triggers batch loader when near bottom
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Checks scroll position and loads next batch if near bottom
   * 
   * Debounced scroll handler that:
   * 1. Clears previous timeout to prevent multiple calls
   * 2. Waits 50ms to batch scroll events
   * 3. Checks if user scrolled within 100px of bottom
   * 4. Checks if batch loader is not already loading
   * 5. Checks if not currently filtering
   * 6. Triggers next batch load if conditions met
   * 
   * Scroll position calculation:
   * - window.innerHeight: Viewport height
   * - window.scrollY: Current scroll position
   * - document.body.offsetHeight: Total page height
   * - 100: Buffer pixels from bottom
   * 
   * @private
   * @method checkScrollAndLoad
   * @returns {void}
   * @side-effect May trigger batch loader, manages scrollTimeout
   */
  private scrollTimeout: any;
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes batch loader for infinite scroll pagination
   * 
   * Creates a new BatchLoader instance configured with:
   * 1. Data source: originalcultureSummaryReports
   * 2. Update callback: Updates cultureSummaryReports with new batch
   * 3. Custom sorting: undefined (uses default/no sorting)
   * 4. Loading indicator: Updates listLoader flag
   * 
   * Batch loader configuration:
   * - Default batch size: 20 items per load
   * - Callback on each batch completion
   * - Loading state management
   * 
   * @private
   * @method initializeBatchLoader
   * @returns {void}
   * @side-effect Creates new batchLoader instance
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.originalcultureSummaryReports,
      (updatedList) => {
        this.cultureSummaryReports = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Opens the culture summary report view in fullscreen mode
   * 
   * Displays the report data and charts in fullscreen using browser's
   * Fullscreen API. Requires element with id 'zoomView' to exist in template.
   * 
   * Fullscreen flow:
   * 1. Gets DOM element with id 'zoomView'
   * 2. Checks if browser supports requestFullscreen
   * 3. Initiates fullscreen mode if supported
   * 
   * @method openFullScreen
   * @returns {void}
   * @side-effect Requests fullscreen mode from browser
   */
  public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * Exports culture summary report data to Excel file
   * 
   * Downloads the report with selected business and farm site as an Excel file.
   * Uses CultureSummaryReportService to generate and download file.
   * 
   * Export parameters:
   * - business_id: Selected business identifier
   * - farm_site_id: Selected farm site identifier
   * 
   * Process flow:
   * 1. Constructs request body with selected criteria
   * 2. Calls service to generate Excel file
   * 3. Subscribes to file blob response
   * 4. Saves file using ExcelService
   * 5. File downloaded with name 'Culture Summary Report'
   * 
   * @async
   * @method exportExcel
   * @returns {Promise<void>}
   * @side-effect Triggers file download
   * @throws {Error} If export fails
   */
  public async exportExcel() {
    const excelReqBody = {
      "business_id": this.cultureSummaryReport.business_id,
      "farm_site_id": this.cultureSummaryReport.farm_site_id,
    }
    try {
      const fileObj = this.cultureSummaryService.excelReportsDownload('cultureSummaryReport', excelReqBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Culture Summary Report');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Generates and displays culture analysis charts
   * 
   * Creates two charts for culture analysis:
   * 1. Feedlog vs DOC: Feed given per day of culture
   * 2. ABW vs DOC: Average body weight per day of culture
   * 
   * Chart data retrieval:
   * 1. Calls PondCultureService to get culture data
   * 2. Extracts feedlog data with DOC labels
   * 3. Extracts sampling data for ABW analysis
   * 4. Prepares chart configuration objects
   * 5. Calls prepareFeedlogvsDoc() to render charts
   * 
   * Chart configuration includes:
   * - Title: Chart description
   * - Labels: DOC (Day of Culture) values
   * - Data: Feed given or ABW values
   * - Type: Line chart
   * - Fill: false for ABW, true for feedlog
   * - Chart ID: Unique canvas element reference
   * 
   * Data preparation:
   * - Feedlog: Iterates through feedlogs array
   * - Sampling: Iterates through samplings array
   * - Handles missing ABW data as 0
   * 
   * No data handling:
   * - Sets myChartNodata flag if feedlog empty
   * - Sets mySamplingvsDocData flag if sampling empty
   * 
   * @async
   * @method diffFeedlogvsDOc
   * @param {string} farmsiteId - Farm site identifier
   * @param {number} pondId - Pond identifier
   * @param {number} cultureId - Culture identifier
   * @returns {Promise<void>}
   * @throws {Error} If service call fails
   * @side-effect Prepares chart data, triggers chart rendering
   */
  public async diffFeedlogvsDOc(farmsiteId: string, pondId: number, cultureId: number) {
    this.myChartNodata = false;
    this.mySamplingvsDocData = false;
    this.responseLoader = true;
    try {
      this.pondCulture = await this.pondCultureService.getCultureSumaryByFarmSite(farmsiteId, pondId, cultureId);
      this.responseLoader = false;
      if (this.pondCulture !== undefined) {
        let docchart = {
          title: 'Feedlog vs DOC',
          lable: [],
          data: [],
          type: 'line',
          fill: true,
          chartid: 'myChart'
        };
        let feedlogDetails = this.pondCulture.feedlogs || [];
        for (let i = 0; i < feedlogDetails.length; i++) {
          let lable = feedlogDetails[i].DOC;
          let data = parseInt(feedlogDetails[i].feedGiven);
          docchart.lable.push(lable);
          docchart.data.push(data);
        }
        // prepare sampling doc charts
        let samplingdocchart = {
          title: 'ABW vs DOC',
          lable: [],
          data: [],
          type: 'line',
          fill: false,
          chartid: 'samplingChart'
        };
        let samplingDetails = this.pondCulture.samplings || [];
        for (let i = 0; i < samplingDetails.length; i++) {
          let lable = samplingDetails[i].Doc;
          let data = samplingDetails[i].ABW ? samplingDetails[i].ABW : 0;
          samplingdocchart.lable.push(lable);
          samplingdocchart.data.push(data);
        }
        if (docchart.data.length > 0) {
          this.myChartNodata = false;
          this.prepareFeedlogvsDoc(docchart);
        } else {
          this.myChartNodata = true;
        }
        if (samplingdocchart.data.length > 0) {
          this.mySamplingvsDocData = false;
          this.prepareFeedlogvsDoc(samplingdocchart);
        } else {
          this.mySamplingvsDocData = true;
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Renders a Chart.js chart on the canvas
   * 
   * Creates and renders a line chart using Chart.js library.
   * Configures chart with provided data, labels, and options.
   * 
   * Chart configuration:
   * - Type: Line chart
   * - Labels: DOC values from data
   * - Datasets: Single dataset with provided data
   * - Border color: Light blue (rgb(84, 216, 255))
   * - Border width: 1px
   * - Fill: true or false based on config
   * - Y-axis: Starts at zero
   * 
   * Process flow:
   * 1. Gets canvas element by chart ID
   * 2. Gets 2D context from canvas
   * 3. Creates new Chart.js instance
   * 4. Configures with labels and datasets
   * 5. Sets responsive scaling options
   * 
   * @method prepareFeedlogvsDoc
   * @param {any} docchart - Chart configuration object containing:
   *   - title: Chart title/label
   *   - lable: Array of DOC labels
   *   - data: Array of values to plot
   *   - type: Chart type ('line')
   *   - fill: Whether to fill area under line
   *   - chartid: Canvas element ID
   * @returns {void}
   * @throws {Error} If canvas element not found
   * @side-effect Renders chart on canvas
   */
  public prepareFeedlogvsDoc(docchart) {
    try {
      this.canvas = document.getElementById(docchart.chartid);
      this.ctx = this.canvas.getContext('2d');
      new Chart(this.ctx, {
        type: docchart.type,
        data: {
          labels: docchart.lable,
          datasets: [{
            label: docchart.title,
            data: docchart.data,
            fill: docchart.fill,
            borderColor: [
              'rgb(84, 216, 255)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true
              }
            }]
          }
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Sorts culture summary reports by specified column
   * 
   * Handles column sorting with ascending/descending toggle.
   * Only one column can be sorted at a time; selecting another clears previous sort.
   * 
   * Supported sort columns:
   * - pondname: Pond/section name
   * - identifier: Culture identifier
   * - startdate: Culture start date
   * - enddate: Culture end date
   * - stockingsize: Stocking count/size
   * 
   * Sort logic:
   * 1. Toggles ascending/descending for selected column
   * 2. Uses lodash orderBy for sorting
   * 3. Clears all other sort flags
   * 4. Updates cultureSummaryReports array
   * 
   * @method sortingTanks
   * @param {string} type - Column to sort by ('pondname', 'identifier', 'startdate', 'enddate', 'stockingsize')
   * @returns {void}
   * @side-effect Updates cultureSummaryReports sort order, updates sort state flags
   */
  public sortingTanks(type: string) {
    if (type == 'pondname') {
      this.nameAsc = !this.nameAsc;
      let orderbyval = 'desc';
      if (this.nameAsc == true) {
        orderbyval = 'asc';
      }
      this.cultureSummaryReports = _.orderBy(this.cultureSummaryReports, ['name'], [orderbyval]);
      this.cultureIdentifierAsc = false;
      this.startDateAsc = false;
      this.endDateAsc = false;
      this.stockingSizeAsc = false;
    }
    if (type == 'identifier') {
      this.cultureIdentifierAsc = !this.cultureIdentifierAsc;
      let orderbyval = 'desc';
      if (this.cultureIdentifierAsc == true) {
        orderbyval = 'asc';
      }
      this.cultureSummaryReports = _.orderBy(this.cultureSummaryReports, ['culture_identifier_x'], [orderbyval]);
      this.nameAsc = false;
      this.startDateAsc = false;
      this.endDateAsc = false;
      this.stockingSizeAsc = false;
    }
    if (type == 'startdate') {
      this.startDateAsc = !this.startDateAsc;
      let orderbyval = 'desc';
      if (this.startDateAsc == true) {
        orderbyval = 'asc';
      }
      this.cultureSummaryReports = _.orderBy(this.cultureSummaryReports, ['culture_start_date'], [orderbyval]);
      this.nameAsc = false;
      this.cultureIdentifierAsc = false;
      this.endDateAsc = false;
      this.stockingSizeAsc = false;
    }
    if (type == 'enddate') {
      this.endDateAsc = !this.endDateAsc;
      let orderbyval = 'desc';
      if (this.endDateAsc == true) {
        orderbyval = 'asc';
      }
      this.cultureSummaryReports = _.orderBy(this.cultureSummaryReports, ['culture_end_date'], [orderbyval]);
      this.nameAsc = false;
      this.cultureIdentifierAsc = false;
      this.startDateAsc = false;
      this.stockingSizeAsc = false;
    }
    if (type == 'stockingsize') {
      this.stockingSizeAsc = !this.stockingSizeAsc;
      let orderbyval = 'desc';
      if (this.stockingSizeAsc == true) {
        orderbyval = 'asc';
      }
      this.cultureSummaryReports = _.orderBy(this.cultureSummaryReports, ['stocking_count'], [orderbyval]);
      this.nameAsc = false;
      this.cultureIdentifierAsc = false;
      this.startDateAsc = false;
      this.endDateAsc = false;
    }
  }

  /**
   * Angular component destruction lifecycle hook
   * 
   * Called once when the component is being destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * Cleans up batch loader resources.
   * 
   * Cleanup operations:
   * - Cancels FarmSiteService HTTP requests
   * - Cancels BusinessEntityService HTTP requests
   * - Cancels CultureSummaryReportService HTTP requests
   * - Cancels PondCultureService HTTP requests
   * - Cancels PersonsService HTTP requests
   * - Marks batch loader as destroyed
   * - Resets batch loader state
   * 
   * Importance:
   * - Prevents memory leaks from pending requests
   * - Reduces unnecessary server load
   * - Improves application performance
   * - Essential for proper component cleanup
   * 
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook - Called once before component is destroyed
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.cultureSummaryService.cancelOnGoingHttpRequests();
    this.pondCultureService.cancelOnGoingHttpRequests();
    this.personService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
